import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initialCustomLabForPreset, noneLayer } from '../../makeup-flux-lab/utils/labMakeupSchema';
import { findCatalogOption } from '../../makeup-flux-lab/utils/catalogOptionMatch';
import { applyFinishToLayer, FLUX_LAB_FINISH_CATEGORIES } from '../../makeup-flux-lab/utils/makeupFluxLabFinishes';
import { categorySelectOptions } from '../../makeup-flux-lab/utils/makeupFluxLabControls';
import { analyzeGuestForFluxLab, resolveStudioMakeupForFluxLook } from '../../makeup-flux-lab/utils/makeupFluxLabApi';
import { studioMakeupToLab } from '../../makeup-flux-lab/utils/studioMakeupToLab';
import { fetchMakeupFluxPromptV3BuildInfo, generateMakeupFluxPromptV3 } from '../utils/makeupFluxPromptV3Api';
import { stripStaleAutoFlags } from '../../makeup-flux-lab/utils/makeupLabResolve';
import {
  formatSkinAnalysisHint,
  hydrateLabFromSkinResolve,
  labNeedsStudioResolve,
  missingSkinAnalysisForLab,
  prepareMakeupLabForGenerate,
} from '../utils/makeupPromptV3SkinFlow';
import { studioPresetForV3Preset } from '../utils/makeupV3StudioMap';
import {
  configurableCategories,
  evaluateMakeupLabAdvisory,
  hasActiveMakeupLab,
} from '../utils/makeupPromptV3Helpers';
import { presetSupportsBridalOrnaments } from '../utils/makeupV3BridalOrnamentStyles';
import {
  bridalLookById,
  MAKEUP_V3_BRIDAL_LOOK_PRESET_ID,
  makeupLabForBridalLook,
} from '../utils/makeupV3BridalLooks';
import {
  MAKEUP_V3_TIME_PRESETS,
  makeupLabForV3Preset,
  presetsForTimeOfUse,
} from '../utils/makeupFluxV3TimePresets';
import { buildMakeupHistoryFromStudio } from '../utils/makeupCompareHistory';

export function useMakeupV3StudioState({ initialSourceImage = '', onResult } = {}) {
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const makeupLabRef = useRef(null);
  const presetIdRef = useRef('');
  const pendingResolveLabRef = useRef(null);
  const skinToneRef = useRef(null);
  const studioResolvedLabRef = useRef(null);
  const lastAnalyzedRef = useRef('');
  /** >0 after any custom edit — blocks ceremony preset prompt boost on generate. */
  const customEditsRef = useRef(0);
  /** Studio resolve snapshot at first custom edit — keeps auto cheek/lip/eye hex stable. */
  const frozenStudioLabRef = useRef(null);
  /** Always the first guest upload — never a prior FLUX result. */
  const guestOriginalRef = useRef(initialSourceImage || '');

  const [sourceImage, setSourceImageState] = useState(initialSourceImage || '');
  const setSourceImage = useCallback((url) => {
    if (url) guestOriginalRef.current = url;
    setSourceImageState(url);
  }, []);
  const [makeupLab, setMakeupLab] = useState(() => initialCustomLabForPreset({}));
  const [wrapIdentity, setWrapIdentity] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resultImage, setResultImage] = useState('');
  const [meta, setMeta] = useState(null);
  const [rev, setRev] = useState('');
  const [applyAutoFoundation, setApplyAutoFoundation] = useState(true);
  const [timeOfUse, setTimeOfUse] = useState('day');
  const [presetId, setPresetId] = useState('');
  const [makeupPresetMode, setMakeupPresetMode] = useState('everyday');
  const [bridalLookId, setBridalLookId] = useState('');
  const [bridalOrnamentStyle, setBridalOrnamentStyle] = useState('none');
  const [skinTone, setSkinTone] = useState(null);
  const [skinBusy, setSkinBusy] = useState(false);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [studioResolvedLab, setStudioResolvedLab] = useState(null);
  const [resolveTick, setResolveTick] = useState(0);

  makeupLabRef.current = makeupLab;
  presetIdRef.current = presetId;
  skinToneRef.current = skinTone;
  studioResolvedLabRef.current = studioResolvedLab;

  const categories = useMemo(() => configurableCategories(), []);
  const skinHint = useMemo(() => formatSkinAnalysisHint(skinTone), [skinTone]);
  const timePresets = useMemo(() => presetsForTimeOfUse(timeOfUse), [timeOfUse]);
  const activePreset = useMemo(() => {
    if (bridalLookId) {
      const look = bridalLookById(bridalLookId);
      if (!look) return null;
      return {
        id: MAKEUP_V3_BRIDAL_LOOK_PRESET_ID,
        label: look.label,
        timeOfUse: 'ceremony',
        description: look.subtitle,
      };
    }
    return MAKEUP_V3_TIME_PRESETS.find((p) => p.id === presetId) || null;
  }, [presetId, bridalLookId]);
  const showBridalOrnaments = useMemo(
    () => presetSupportsBridalOrnaments(presetId),
    [presetId],
  );
  const advisory = useMemo(() => evaluateMakeupLabAdvisory(makeupLab), [makeupLab]);
  const labActive = useMemo(() => hasActiveMakeupLab(makeupLab), [makeupLab]);
  const selectOptions = useMemo(() => {
    const out = {};
    for (const cat of categories) {
      out[cat] = categorySelectOptions(cat, makeupLab[cat]);
    }
    return out;
  }, [categories, makeupLab]);

  useEffect(() => {
    fetchMakeupFluxPromptV3BuildInfo()
      .then((d) => setRev(d?.rev || ''))
      .catch(() => {});
  }, []);

  const bumpResolve = useCallback(() => setResolveTick((t) => t + 1), []);

  const clearStudioResolve = useCallback(() => {
    studioResolvedLabRef.current = null;
    setStudioResolvedLab(null);
  }, []);

  /** Sync ref + state after edits; avoid resolve race that re-hydrates preset over None picks. */
  const commitLab = useCallback(
    (nextLab) => {
      makeupLabRef.current = nextLab;
      setMakeupLab(nextLab);
      if (!hasActiveMakeupLab(nextLab)) {
        clearStudioResolve();
        setPresetId('');
        setBridalLookId('');
        setMakeupPresetMode('everyday');
        setBridalOrnamentStyle('none');
        setResultImage('');
        setMeta(null);
        setError('All categories are None — pick at least one to apply makeup.');
        return;
      }
      setError('');
      if (labNeedsStudioResolve(nextLab) && customEditsRef.current === 0) {
        bumpResolve();
      } else if (!labNeedsStudioResolve(nextLab)) {
        clearStudioResolve();
      }
    },
    [bumpResolve, clearStudioResolve],
  );

  const runSkinAnalyze = useCallback(
    async (url) => {
      if (!url || lastAnalyzedRef.current === url) return;
      lastAnalyzedRef.current = url;
      setSkinTone(null);
      studioResolvedLabRef.current = null;
      setStudioResolvedLab(null);
      setSkinBusy(true);
      setError('');
      try {
        const analysis = await analyzeGuestForFluxLab(url);
        setSkinTone(analysis?.success ? analysis : null);
        if (!analysis?.success) {
          setError(analysis?.error || 'Could not analyze skin tone (Gemma vision).');
        } else {
          bumpResolve();
        }
      } catch (e) {
        setError(e.message || 'Skin analysis failed');
      } finally {
        setSkinBusy(false);
      }
    },
    [bumpResolve],
  );

  useEffect(() => {
    if (!initialSourceImage) return;
    guestOriginalRef.current = initialSourceImage;
    setSourceImage(initialSourceImage);
    setResultImage('');
    setMeta(null);
    lastAnalyzedRef.current = '';
    runSkinAnalyze(initialSourceImage);
  }, [initialSourceImage, runSkinAnalyze]);

  useEffect(() => {
    if (!skinTone?.success || !skinTone.skin_profile || !skinTone.lip_profile) {
      setStudioResolvedLab(null);
      return undefined;
    }
    const lab = pendingResolveLabRef.current ?? makeupLabRef.current;
    pendingResolveLabRef.current = null;
    if (!labNeedsStudioResolve(lab)) {
      clearStudioResolve();
      return undefined;
    }
    let cancelled = false;
    const requestedPreset = presetIdRef.current;
    const labSnapshot = lab;
    const editsAtStart = customEditsRef.current;
    setResolveBusy(true);
    const studioId = studioPresetForV3Preset(requestedPreset);
    resolveStudioMakeupForFluxLook({
      fluxLookId: requestedPreset || 'v3-custom',
      studioPresetId: studioId,
      skinProfile: skinTone.skin_profile,
      lipProfile: skinTone.lip_profile,
    })
      .then((resolved) => {
        if (cancelled || editsAtStart !== customEditsRef.current) return;
        const studioLab = studioMakeupToLab(resolved?.makeup);
        setStudioResolvedLab(studioLab);
        if (customEditsRef.current > 0) return;
        const hydrated = hydrateLabFromSkinResolve(
          makeupLabRef.current || labSnapshot,
          studioLab,
          skinTone,
        );
        if (editsAtStart !== customEditsRef.current) return;
        makeupLabRef.current = hydrated;
        setMakeupLab(hydrated);
      })
      .catch(() => {
        if (!cancelled) setStudioResolvedLab(null);
      })
      .finally(() => {
        if (!cancelled) setResolveBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [skinTone, resolveTick, presetId, clearStudioResolve]);

  const clearPresetSelection = useCallback(() => {
    setPresetId('');
    setBridalLookId('');
    setMakeupPresetMode('everyday');
    setBridalOrnamentStyle('none');
    customEditsRef.current = 0;
    frozenStudioLabRef.current = null;
  }, []);

  const applyBridalLook = useCallback(
    (lookId) => {
      const look = bridalLookById(lookId);
      if (!look) return;
      setMakeupPresetMode('bridal');
      setBridalLookId(lookId);
      setPresetId(MAKEUP_V3_BRIDAL_LOOK_PRESET_ID);
      setBridalOrnamentStyle(look.ornamentId || 'none');
      customEditsRef.current = 0;
      frozenStudioLabRef.current = null;
      const nextLab = makeupLabForBridalLook();
      pendingResolveLabRef.current = nextLab;
      makeupLabRef.current = nextLab;
      setMakeupLab(nextLab);
      setTimeOfUse('ceremony');
      setError('');
      bumpResolve();
    },
    [bumpResolve],
  );

  const applyPreset = useCallback(
    (id) => {
      setMakeupPresetMode('everyday');
      setBridalLookId('');
      setPresetId(id);
      if (!id) {
        setBridalOrnamentStyle('none');
        return;
      }
      customEditsRef.current = 0;
      frozenStudioLabRef.current = null;
      const preset = MAKEUP_V3_TIME_PRESETS.find((p) => p.id === id);
      if (!preset) return;
      setBridalOrnamentStyle('none');
      const nextLab = makeupLabForV3Preset(preset);
      pendingResolveLabRef.current = nextLab;
      makeupLabRef.current = nextLab;
      setMakeupLab(nextLab);
      setTimeOfUse(preset.timeOfUse);
      setError('');
      bumpResolve();
    },
    [bumpResolve],
  );

  const onTimeOfUseChange = useCallback((nextTime) => {
    setTimeOfUse(nextTime);
    setPresetId('');
    setBridalLookId('');
    setMakeupPresetMode('everyday');
    setBridalOrnamentStyle('none');
  }, []);

  const onMakeupPresetModeChange = useCallback((mode) => {
    setMakeupPresetMode(mode);
    if (mode === 'everyday') {
      setBridalLookId('');
      if (presetId === MAKEUP_V3_BRIDAL_LOOK_PRESET_ID) {
        setPresetId('');
        setBridalOrnamentStyle('none');
      }
    } else {
      setPresetId('');
    }
  }, [presetId]);

  const bumpCustomEdits = useCallback(() => {
    if (customEditsRef.current === 0 && studioResolvedLabRef.current) {
      frozenStudioLabRef.current = studioResolvedLabRef.current;
    }
    customEditsRef.current += 1;
  }, []);

  const onCategory = useCallback(
    (category, optionId) => {
      bumpCustomEdits();
      const opt = findCatalogOption(category, optionId);
      const prev = makeupLabRef.current || makeupLab;
      const next = { ...prev };
      if (!opt || optionId === 'none') {
        next[category] = noneLayer();
      } else {
        next[category] = stripStaleAutoFlags({ ...opt.layer }, category);
      }
      commitLab(next);
    },
    [bumpCustomEdits, commitLab, makeupLab],
  );

  const onIntensity = useCallback(
    (category, value) => {
      bumpCustomEdits();
      const n = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
      const prev = makeupLabRef.current || makeupLab;
      const layer = prev[category];
      if (!layer || layer.disabled) return;
      const next =
        n <= 0
          ? { ...prev, [category]: noneLayer() }
          : { ...prev, [category]: { ...layer, intensity: n } };
      commitLab(next);
    },
    [bumpCustomEdits, commitLab, makeupLab],
  );

  const onFinish = useCallback(
    (category, finishId) => {
      if (!FLUX_LAB_FINISH_CATEGORIES.includes(category)) return;
      bumpCustomEdits();
      const prev = makeupLabRef.current || makeupLab;
      const layer = prev[category];
      if (!layer || layer.disabled) return;
      const next = {
        ...prev,
        [category]: stripStaleAutoFlags(applyFinishToLayer(layer, finishId, category), category),
      };
      commitLab(next);
    },
    [bumpCustomEdits, commitLab, makeupLab],
  );

  const onGenerate = useCallback(async () => {
    const img = guestOriginalRef.current || initialSourceImage || sourceImage;
    if (!img) {
      setError('Upload a photo first.');
      return;
    }
    const currentLab = makeupLabRef.current;
    const currentSkin = skinToneRef.current;
    const currentStudio = studioResolvedLabRef.current;
    if (!hasActiveMakeupLab(currentLab)) {
      setError('Pick at least one category (not all None).');
      return;
    }
    if (missingSkinAnalysisForLab(currentLab, currentSkin)) {
      setError('Wait for Gemma skin analysis (needed for Auto foundation / cheek / lip).');
      return;
    }
    if (resolveBusy && customEditsRef.current === 0) {
      setError('Still matching preset colors to guest skin…');
      return;
    }
    const studioForMerge =
      customEditsRef.current > 0
        ? frozenStudioLabRef.current || currentStudio
        : currentStudio || null;
    const labForFlux = prepareMakeupLabForGenerate(currentLab, currentSkin, studioForMerge);
    const customLabEdits = customEditsRef.current;
    if (!hasActiveMakeupLab(labForFlux)) {
      setError('Pick at least one category (not all None).');
      return;
    }
    const activePresetId = presetIdRef.current || '';
    const usePresetPromptBoost =
      Boolean(activePresetId) && hasActiveMakeupLab(currentLab) && customLabEdits === 0;
    const activeBridalLook =
      bridalLookId &&
      (makeupPresetMode === 'bridal' || presetIdRef.current === MAKEUP_V3_BRIDAL_LOOK_PRESET_ID)
        ? bridalLookId
        : '';
    const resolvedBridalOrnament = activeBridalLook
      ? bridalOrnamentStyle && bridalOrnamentStyle !== 'none'
        ? bridalOrnamentStyle
        : undefined
      : presetSupportsBridalOrnaments(activePresetId) &&
          bridalOrnamentStyle &&
          bridalOrnamentStyle !== 'none'
        ? bridalOrnamentStyle
        : undefined;
    setBusy(true);
    setError('');
    try {
      const data = await generateMakeupFluxPromptV3({
        image: img,
        makeupLab: labForFlux,
        skinTone: currentSkin?.success ? currentSkin : undefined,
        wrapIdentity,
        applyAutoFoundation,
        presetId: activeBridalLook ? MAKEUP_V3_BRIDAL_LOOK_PRESET_ID : activePresetId || undefined,
        usePresetPromptBoost: activeBridalLook ? false : usePresetPromptBoost,
        customLabEdits,
        timeOfUse: activeBridalLook ? 'ceremony' : activePreset?.timeOfUse || timeOfUse,
        bridalOrnamentStyle: makeupPresetMode === 'bridal' ? resolvedBridalOrnament : undefined,
        bridalLookId: activeBridalLook || undefined,
      });
      const out = data.image || '';
      setResultImage(out);
      setMeta(data.meta || null);
      if (out && onResultRef.current) {
        const historyMeta = buildMakeupHistoryFromStudio({
          activePreset,
          timeOfUse: activePreset?.timeOfUse || timeOfUse,
          makeupLab: labForFlux,
          usedPresetId: Boolean(activePresetId),
          meta: data.meta || null,
        });
        await onResultRef.current(out, historyMeta);
      }
    } catch (e) {
      setError(e.message || 'Generation failed');
      setResultImage('');
      setMeta(null);
    } finally {
      setBusy(false);
    }
  }, [
    sourceImage,
    initialSourceImage,
    resolveBusy,
    wrapIdentity,
    applyAutoFoundation,
    presetId,
    activePreset,
    timeOfUse,
    bridalOrnamentStyle,
    bridalLookId,
    makeupPresetMode,
  ]);

  const resetResults = useCallback(() => {
    setResultImage('');
    setMeta(null);
    setError('');
  }, []);

  return {
    sourceImage,
    setSourceImage,
    setError,
    runSkinAnalyze,
    resetResults,
    makeupLab,
    wrapIdentity,
    setWrapIdentity,
    busy,
    error,
    resultImage,
    meta,
    rev,
    applyAutoFoundation,
    setApplyAutoFoundation,
    timeOfUse,
    onTimeOfUseChange,
    presetId,
    applyPreset,
    applyBridalLook,
    clearPresetSelection,
    makeupPresetMode,
    onMakeupPresetModeChange,
    bridalLookId,
    bridalOrnamentStyle,
    setBridalOrnamentStyle,
    showBridalOrnaments,
    timePresets,
    activePreset,
    skinTone,
    skinBusy,
    resolveBusy,
    skinHint,
    advisory,
    labActive,
    categories,
    selectOptions,
    onCategory,
    onIntensity,
    onFinish,
    onGenerate,
  };
}
